<template>
  <v-app>
    <app-error-messages />
    <app-upload-tray />
    <v-main>
      <v-app-bar
        v-if="$vuetify.display.smAndDown"
        :elevation="2"
        color="primary"
      >
        <template #image>
          <v-img
            class="ma-3"
            :class="{'v-app-bar__mobile' : $capacitor.isNativeApp()}"
            max-height="38"
            src="@/images/logo-white.svg"
            width="117"
          />
        </template>
      </v-app-bar>
      <v-navigation-drawer
        v-model="drawer"
        class="sidebar"
        width="300"
      >
        <v-row no-gutters>
          <v-col
            class="ma-2"
            :class="{'v-navigation-drawer__mobile' : $capacitor.isNativeApp()}"
          >
            <v-img
              class="mb-6 ml-2 mt-4"
              max-height="38"
              src="@/images/logo.svg"
              width="128"
            />
            <v-text-field
              v-model="search"
              class="bg-white border border-opacity-25 rounded-pill mb-4"
              density="compact"
              flat
              hide-details
              placeholder="Search for files"
              type="search"
              variant="solo"
            >
              <template #prepend-inner>
                <v-icon icon="mdi-magnify" />
              </template>
            </v-text-field>
          </v-col>
        </v-row>
        <!-- menu items -->
        <template v-if="search === ''">
          <v-list-item
            v-for="( item, index) in menu"
            :key="index"
            active-class="bg-primary"
            class="sidebar__item mx-2 mb-1"
            rounded="pill"
            :to="$routeTo(item.route)"
          >
            <v-list-item-title class="sidebar__title font-mono">
              {{ item.title }}
            </v-list-item-title>
          </v-list-item>
        </template>
        <!-- search -->
        <app-auth-layout-search-files
          v-else
          :search="search"
          @clear="search = ''"
        />
        <!-- footer -->
        <template
          v-if="search === ''"
          #append
        >
          <template v-if="$auth.hasActiveSubscription && $vuetify.display.mdAndUp && $auth.user?.user_type === userTypes.USER">
            <app-account-quota />
          </template>
          <v-list>
            <v-list-item
              v-if="$auth.user?.user_type !== userTypes.ADMIN"
              :href="$vuetify.display.mdAndUp ? supportMailto : undefined"
            >
              <div class="d-flex align-center">
                <v-icon
                  class="mr-2"
                  icon="mdi-lifebuoy"
                />
                <v-list-item-title class="sidebar__title font-mono">
                  Support
                </v-list-item-title>
              </div>
            </v-list-item>
            <v-list-item
              v-if="$auth.user?.user_type !== userTypes.ADMIN"
              :to="$auth.user?.user_type === userTypes.BENEFICIARY ? $routeTo(ROUTES.BENEFICIARY_MY_ACCOUNT) : $routeTo($auth.user?.user_type === userTypes.BENEFICIARY ? ROUTES.BENEFICIARY_MY_ACCOUNT : ROUTES.MY_ACCOUNT)"
            >
              <div class="d-flex align-center">
                <v-icon
                  class="mr-2"
                  icon="mdi-cog-outline"
                />
                <v-list-item-title class="sidebar__title font-mono">
                  Settings
                </v-list-item-title>
              </div>
            </v-list-item>
            <v-list-item
              v-if="$auth.user?.is_impersonated"
              :disabled="loading"
              @click="logoutImpersonation"
            >
              <div class="d-flex align-center">
                <template v-if="!loading">
                  <v-icon
                    class="mr-2"
                    color="red"
                    icon="mdi-logout"
                  />
                </template>
                <template v-else>
                  <v-progress-circular
                    class="mr-2"
                    color="red"
                    indeterminate
                    size="20"
                  />
                </template>
                <v-list-item-title class="font-weight-bold text-red text-subtitle-2 mr-2">
                  Logout Impersonation
                </v-list-item-title>
              </div>
            </v-list-item>
          </v-list>
          <v-divider class="my-2 border-opacity-100" />
          <v-list-item class="px-3 py-2">
            <div class="d-flex justify-space-between">
              <div class="d-flex">
                <v-avatar
                  v-if="$auth.user?.profile_file_id"
                  class="me-3"
                  size="40"
                >
                  <v-img :src="$file.url($auth.user?.profile_file_id)" />
                </v-avatar>
                <div>
                  <div class="sidebar__name font-mono">
                    {{ name }}
                  </div>
                  <div class="text-caption text-grey text-secondary">
                    {{ email }}
                  </div>
                </div>
              </div>
              <div class="d-flex flex-column align-end justify-end">
                <v-icon
                  class="text-primary ms-auto text-secondary"
                  icon="mdi-logout"
                  variant="plain"
                  @click.prevent="logout"
                />
                <p class="sidebar__logout">
                  Log out
                </p>
              </div>
            </div>
            <v-spacer />
          </v-list-item>
        </template>
      </v-navigation-drawer>
      <v-icon
        class="d-none d-md-block mx-4 my-2 text-secondary"
        icon="mdi-menu"
        @click.stop="drawer = !drawer"
      />
      <v-container :fluid="!$capacitor.isNativeApp() && !drawer">
        <slot />
      </v-container>
      <v-bottom-navigation
        color="primary"
        class="d-md-none"
        active
        height="64"
        :class="{'v-bottom-navigation__mobile' : $capacitor.isNativeApp()}"
      >
        <v-btn
          v-for="( item, index) in menu"
          :key="index"
          :to="$routeTo(item.route)"
        >
          <v-icon color="primary">
            {{ item.icon }}
          </v-icon>

          {{ item.title }}
        </v-btn>

        <v-btn
          v-if="$auth.user?.user_type !== userTypes.ADMIN"
          :to="$routeTo(settings)"
        >
          <v-icon color="primary">
            mdi-cog
          </v-icon>

          Settings
        </v-btn>
      </v-bottom-navigation>
    </v-main>
  </v-app>
</template>

<script lang="ts">
import icons from "@/plugins/icons"
import {defineComponent} from "vue"
import {UserTypes} from "@/enums/UserTypes"
import AppAccountQuota from "@/components/AppAccountQuota.vue"
import AppErrorMessages from "@/components/AppErrorMessages.vue"
import AppUploadTray from "@/components/AppUploadTray.vue"
import AppAuthLayoutSearchFiles from "@/components/AppAuthLayoutSearchFiles.vue"
import {useLayoutStore} from "@/stores/layout"

export default defineComponent({
  components: {
    AppAccountQuota,
    AppErrorMessages,
    AppUploadTray,
    AppAuthLayoutSearchFiles,
  },
  mixins: [icons],
  setup() {
    const layout = useLayoutStore()

    return { layout }
  },
  data() {
    return {
      search: '' as string,
      loading: false as boolean,
      options: [
        {
          title: 'Home',
          route: this.ROUTES.DASHBOARD,
          user_type: UserTypes.USER,
          icon: 'mdi-home'
        },
        {
          title: 'My Files',
          route: this.ROUTES.FOLDERS,
          user_type: UserTypes.USER,
          icon: 'mdi-file-multiple'
        },
        {
          title: 'Beneficiaries',
          route: this.ROUTES.BENEFICIARIES,
          user_type: UserTypes.USER,
          icon: 'mdi-account-heart'
        },
        {
          title: 'Folders (Shared)',
          route: this.ROUTES.BENEFICIARY_FOLDERS,
          user_type: UserTypes.BENEFICIARY,
          icon: 'mdi-file-multiple'
        },
        {
          title: 'Dashboard',
          route: this.ROUTES.ADMIN_DASHBOARD,
          user_type: UserTypes.ADMIN,
          icon: 'mdi-home'
        },
        {
          title: 'Users',
          route: this.ROUTES.ADMIN_MANAGE_USERS,
          user_type: UserTypes.ADMIN,
          icon: 'mdi-account-group'
        },
        {
          title: 'Beneficiaries',
          route: this.ROUTES.ADMIN_MANAGE_BENEFICIARIES,
          user_type: UserTypes.ADMIN,
          icon: 'mdi-account-heart'
        },
        {
          title: 'Admins',
          route: this.ROUTES.ADMIN_MANAGE_ADMINS,
          user_type: UserTypes.ADMIN,
          icon: 'mdi-security'
        },
      ],
      userTypes: UserTypes,
    }
  },
  computed: {
    supportMailto(): string {
      const subject = encodeURIComponent('Support Request: Long Live')
      const body = encodeURIComponent('Hi, I need help with...')

      return `mailto:malachiekel@ourlastwords.org?subject=${subject}&body=${body}`
    },
    drawer: {
      get() {
        return this.layout.drawer
      },
      set(value: boolean) {
        this.layout.setDrawer(value)
      }
    },
    menu() {
      return this.options.filter(item => item.user_type === this.$auth.user?.user_type)
    },
    email() {
      if (!this.$auth.user) {
        return ''
      }

      const email = this.$auth.user.email

      return email.length > 20
        ? email.slice(0, 20) + '...'
        : email
    },
    name() {
      if (!this.$auth.user) {
        return ''
      }

      const name = this.$auth.user.full_name

      return name.length > 20
        ? name.slice(0, 20) + '...'
        : name
    },
    settings() {
      return this.$auth.user?.user_type !== UserTypes.USER
          ? this.ROUTES.BENEFICIARY_MY_ACCOUNT
          : this.ROUTES.MY_ACCOUNT
    }
  },
  mounted() {
    // this.$requestPushPermissions()
    if (this.$vuetify.display.smAndDown) {
      this.drawer = false
    }
  },
  methods: {
    async logout() {
      await this.$auth.logout()

      await this.$router.push(this.$routeTo(this.ROUTES.LOGIN))
    },
    async logoutImpersonation() {
      this.loading = true

      const {
        data: {access_token, message, errors},
        status
      } = await this.$http.delete("/auth/stop-impersonating").catch((e) => e)

      if (this.$error(status, message, errors)) {
        this.loading = false

        return
      }

      await this.$auth.loginUsingToken(access_token, true)

      await this.$router.push(this.$routeTo(this.ROUTES.ADMIN_DASHBOARD))

      this.loading = false
    },
  }
})
</script>

<style lang="scss" scoped>
.sidebar {
  background: rgb(var(--v-theme-newspaper)) !important;
  border-right: 1px solid #EAECF0;

  &__item {
    min-height: 40px;

    &.v-list-item--active > :deep(.v-list-item__overlay) {
      opacity: 0;
    }
  }

  &__title,
  &__name {
    font-weight: 500;
  }

  &__title {
    font-size: 16px;
  }

  &__name {
    font-size: 14px;
  }

  &__logout {
    color: rgba(var(--v-theme-secondary)) !important;
    font-size: 10px;
  }
}

.v-bottom-navigation__mobile {
  height: calc(64px + var(--v-safe-area-bottom)) !important;
  padding-bottom: calc(var(--v-safe-area-bottom) - 8px) !important;
}
</style>
